import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Calendar,
  Clock,
  User,
  FileText,
  CheckCircle,
  AlertCircle,
  PlayCircle,
  Edit,
} from "lucide-react";
import { Id } from "../../../../../../convex/_generated/dataModel";

interface Project {
  _id: Id<"locationProjects">;
  name: string;
  description?: string;
  status: "In Progress" | "Scheduled" | "Completed";
  startDate: string;
  dueDate: string;
  assignedTo: Id<"employees">[];
  assignedToNames?: string[];
  assignedSubcontractors?: Id<"subcontractors">[];
  assignedSubcontractorNames?: string[];
  createdAt: number;
  updatedAt: number;
}

interface ProjectDetailsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onEdit?: () => void;
}

export function ProjectDetailsSheet({
  isOpen,
  onClose,
  project,
  onEdit,
}: ProjectDetailsSheetProps) {
  if (!project) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "In Progress":
        return <PlayCircle className="h-4 w-4 text-yellow-600" />;
      case "Scheduled":
        return <AlertCircle className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-700 border-green-200";
      case "In Progress":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "Scheduled":
        return "bg-blue-100 text-blue-700 border-blue-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const calculateDaysRemaining = () => {
    try {
      const today = new Date();
      const dueDate = new Date(project.dueDate);
      const diffTime = dueDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return { days: Math.abs(diffDays), status: "overdue" };
      } else if (diffDays === 0) {
        return { days: 0, status: "due-today" };
      } else {
        return { days: diffDays, status: "upcoming" };
      }
    } catch {
      return null;
    }
  };

  const daysRemaining = calculateDaysRemaining();

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="text-xl font-semibold">{project.name}</SheetTitle>
          <SheetDescription className="mt-2">
            View detailed information about this project
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Status and Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(project.status)}
                <Badge variant="outline" className={`${getStatusColor(project.status)} border`}>
                  {project.status}
                </Badge>
              </div>
              {onEdit && (
                <Button variant="outline" size="sm" onClick={onEdit} className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit Project
                </Button>
              )}
            </div>

            {/* Project Overview */}
            <Card className="p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <FileText className="h-4 w-4" />
                Project Overview
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Description</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {project.description || "No description provided"}
                  </p>
                </div>
              </div>
            </Card>

            {/* Timeline */}
            <Card className="p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Calendar className="h-4 w-4" />
                Timeline
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Start Date</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(project.startDate)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Due Date</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(project.dueDate)}</p>
                  </div>
                </div>

                {daysRemaining && (
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium">
                        {daysRemaining.status === "overdue" && (
                          <span className="text-red-600">
                            Overdue by {daysRemaining.days} day{daysRemaining.days !== 1 ? "s" : ""}
                          </span>
                        )}
                        {daysRemaining.status === "due-today" && (
                          <span className="text-orange-600">Due today</span>
                        )}
                        {daysRemaining.status === "upcoming" && (
                          <span className="text-gray-600">
                            {daysRemaining.days} day{daysRemaining.days !== 1 ? "s" : ""} remaining
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Assigned Team */}
            <Card className="p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <User className="h-4 w-4" />
                Assigned Team
              </h3>
              <div className="space-y-4">
                {/* Employees Section */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-700">Employees</h4>
                  {project.assignedToNames && project.assignedToNames.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {project.assignedToNames.map((name, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="border-blue-200 bg-blue-50 text-blue-700"
                        >
                          {name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No employees assigned</p>
                  )}
                </div>

                {/* Subcontractors Section */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-700">Subcontractors</h4>
                  {project.assignedSubcontractorNames &&
                  project.assignedSubcontractorNames.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {project.assignedSubcontractorNames.map((name, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="border-green-200 bg-green-50 text-green-700"
                        >
                          {name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No subcontractors assigned</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Project Metadata */}
            <Card className="p-4">
              <h3 className="mb-3 font-semibold">Project Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Created</span>
                  <span className="text-gray-900">
                    {new Date(project.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated</span>
                  <span className="text-gray-900">
                    {new Date(project.updatedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
